import { createFile, ItemsEntity, User, UserRole } from '@citrite/sf-api';
import {
	ProjectsContactsCapabilities,
	projectsExtensionSlots,
	ProjectUserRole,
	ResourceEntityType,
	ShareFileUserDto,
} from '@sharefiledev/projects-pilet-sdk';
import { Capabilities } from '@sharefiledev/sfplugin-capability-contracts';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { shouldDisableLegacyRoles } from '../constants';
import { DefaultProjectDataProvider } from '../PluginDataProvider/DefaultProjectDataProvider';
import { uploadFiles } from './uploadFiles';

export const projectsCapabilities: Capabilities = {
	async provideResourceId(requestedByResourceId) {
		const projectRid = getProjectRid(requestedByResourceId);
		return projectRid.toString();
	},
	getFileActions(requestedByResourceId) {
		return {
			async uploadFiles(files, options) {
				return uploadFiles(
					DefaultProjectDataProvider,
					requestedByResourceId,
					files,
					options
				);
			},
			async deleteFiles(...resourceIds) {
				const projectId = getProjectIdFromRid(requestedByResourceId);
				const fileIds = getFileIdsFromRids(resourceIds);
				await DefaultProjectDataProvider.deleteProjectFiles(projectId, fileIds);
			},
			async getDownloadUrl(...resourceIds) {
				const projectId = getProjectIdFromRid(requestedByResourceId);
				const fileIds = getFileIdsFromRids(resourceIds);
				const downloadSpec =
					await DefaultProjectDataProvider.getProjectFilesBulkDownloadUrl(
						projectId,
						fileIds
					);
				if (!downloadSpec.DownloadUrl) {
					throw new Error('No download url returned');
				}
				return downloadSpec.DownloadUrl;
			},
			async getContentViewerProps(...resourceIds) {
				const resourceId = resourceIds[0];
				const fileRid = RID.parse(resourceId).findEntityType(
					ResourceEntityType.fileStream
				);
				if (!fileRid?.entityId) {
					throw new Error(`No stream id found for: ${resourceId}`);
				}
				const itemUrl = ItemsEntity.get(fileRid.entityId).getUrl();
				const contentViewerProps: any = {
					item: createFile({
						url: itemUrl,
						FileName: '',
						Name: '',
					}),
					isNativeWorkspace: false,
					isWorkspace: false,
					hideNote: true,
					disableEditFileName: true,
				};
				return contentViewerProps;
			},
		};
	},
	async getAuthorizedUserIds(requestedByResourceId) {
		const projectId = getProjectIdFromRid(requestedByResourceId);
		const projectUsers = await DefaultProjectDataProvider.getProjectUsers(projectId);
		return projectUsers.map(({ user }) => user.id);
	},
	async getNavigationDetails(requestedByResourceId, options = {}) {
		const projectId = getProjectIdFromRid(requestedByResourceId);
		const projectEntity = await DefaultProjectDataProvider.getProjectById(projectId);
		let webAppPath = `/projects/${projectEntity.id}`;
		const extensionSlot = options.targetExtensionSlot ?? projectsExtensionSlots.tabs;

		if (extensionSlot === projectsExtensionSlots.tabs && options.targetExtensionSlotId) {
			webAppPath = `${webAppPath}/${options.targetExtensionSlotId}`;
		}

		return {
			name: projectEntity.name,
			webAppPath,
		};
	},
};

function getProjectRid(resourceId: string) {
	const rid = RID.parse(resourceId);
	const projectRid = rid.findEntityType(ResourceEntityType.project);
	if (!projectRid) {
		throw new Error(`No project resource found for: ${resourceId}`);
	}
	return projectRid;
}

const getProjectIdFromRid = (resourceId: string) => getProjectRid(resourceId).entityId;

const getFileIdsFromRids = (resourceIds: string[]) =>
	resourceIds
		.map(rid => {
			const file = RID.parse(rid).findEntityType(ResourceEntityType.file);
			return file?.entityId;
		})
		.filter(Boolean) as string[];

/**
 * @param users Only the Id field is required. Pass full objects only if you already have them, don't fetch
 */
async function addUsersToProject(
	requestedByResourceId: string,
	users: User[],
	projectUserRoleToAdd?: ProjectUserRole
) {
	const projectId = getProjectIdFromRid(requestedByResourceId);
	const projectUsers = await DefaultProjectDataProvider.getProjectUsers(projectId);
	const usersToAdd = [];

	for (const user of users) {
		const userId = user.Id;
		if (!userId) {
			// Invalid user id
			continue;
		}
		if (projectUsers.some(existingUser => existingUser.user.id === userId)) {
			// Already on the project
			continue;
		}

		let projectUserRole = ProjectUserRole.Client;
		if (projectUserRoleToAdd) {
			// If project user role is provided in params then use that
			projectUserRole = projectUserRoleToAdd;
		} else if (
			user.Roles &&
			user.Roles.includes(UserRole.Employee) &&
			!shouldDisableLegacyRoles()
		) {
			// Counterpart is referred as Team Member in the UI
			projectUserRole = ProjectUserRole.Counterpart;
		} else if (!user.Roles) {
			try {
				const role = await DefaultProjectDataProvider.getRoleByUser(userId);
				if (role !== UserRole.Client) {
					projectUserRole = ProjectUserRole.Counterpart;
				}
			} catch (_error) {
				/** @todo log this */
			}
		}

		usersToAdd.push({ user: { id: userId }, role: projectUserRole });
	}
	if (!usersToAdd.length) {
		return [];
	}
	try {
		await DefaultProjectDataProvider.addUsersToProject(projectId, {
			projectUsers: usersToAdd,
		});
	} catch (_error) {
		/** @todo log this */
	}

	return usersToAdd.map(u => u.user.id);
}

export const projectsContactsCapabilities: ProjectsContactsCapabilities = {
	async getProjectContacts(rid: string): Promise<ShareFileUserDto[]> {
		try {
			return await DefaultProjectDataProvider.getProjectContacts(rid);
		} catch {
			throw new Error('Could not get contacts');
		}
	},
};
